import React, { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, X, Package, Truck, History } from "lucide-react";
import { ColorBadge } from "@/components/ColorBadge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export function ServeOrderForm({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serving, setServing] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [error, setError] = useState(null);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchOrderDetails();
    fetchDeliveryHistory();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          date,
          status,
          preparation_status,
          total,
          customer:customers(
            id,
            name
          ),
          order_items(
            id,
            quantity,
            pending_quantity,
            delivered_quantity,
            preparation_quantity,
            preparation_status,
            price,
            last_delivery_date,
            delivery_count,
            product:products(
              id,
              code,
              description,
              images,
              main_image_index,
              inventory(
                id,
                quantity,
                size,
                color
              )
            ),
            size,
            color
          )
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;

      // Verificar que haya al menos una línea preparada que se pueda servir
      const hasServableItems = data.order_items.some(item => 
        item.preparation_quantity > item.delivered_quantity
      );

      if (!hasServableItems) {
        throw new Error("No hay líneas preparadas disponibles para servir");
      }

      // Inicializar las cantidades a servir con las cantidades máximas servibles
      const initialQuantities = {};
      data.order_items.forEach(item => {
        // Solo permitir servir hasta la cantidad preparada menos la ya entregada
        const maxServable = Math.max(0, item.preparation_quantity - item.delivered_quantity);
        initialQuantities[item.id] = maxServable;
      });
      
      setQuantities(initialQuantities);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError(error.message || "No se pudieron cargar los detalles del pedido");
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los detalles del pedido",
        variant: "destructive"
      });
      setTimeout(() => {
        onClose();
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryHistory = async () => {
    try {
      // Buscar entregas previas para este pedido
      const { data: deliveries, error } = await supabase
        .from('order_deliveries')
        .select(`
          id,
          delivery_date,
          delivery_number,
          delivery_type,
          notes,
          created_by,
          document:documents(
            id,
            number,
            total
          ),
          delivery_items(
            quantity_delivered,
            order_item:order_items(
              size,
              color,
              product:products(code, description)
            )
          )
        `)
        .eq('order_id', orderId)
        .order('delivery_date', { ascending: false });

      if (error) throw error;
      setDeliveryHistory(deliveries || []);
    } catch (error) {
      console.error('Error fetching delivery history:', error);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    const item = order.order_items.find(i => i.id === itemId);
    const maxServable = Math.max(0, item.preparation_quantity - item.delivered_quantity);
    const newValue = Math.min(Math.max(0, parseInt(value) || 0), maxServable);

    setQuantities(prev => ({
      ...prev,
      [itemId]: newValue
    }));
  };

  // Función para calcular el importe total de la entrega actual
  const calculateDeliveryTotal = () => {
    return order.order_items.reduce((sum, item) => {
      const quantityToServe = quantities[item.id] || 0;
      return sum + (item.price * quantityToServe);
    }, 0);
  };



  const generateDeliveryNumber = () => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 1000);
    return `ENT-${order.number}-${timestamp}-${random}`;
  };

  const handleServe = async () => {
    try {
      setError(null);
      setServing(true);

      // Obtener usuario actual una sola vez
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      // Preparar todos los datos en memoria antes de hacer operaciones de BD
      const itemsToProcess = [];
      const documentItems = [];
      const deliveryItems = [];
      let documentTotal = 0;
      let allItemsCompleted = true;

      // Validar líneas preparadas y preparar datos en una sola pasada
      for (const item of order.order_items) {
        const quantityToServe = quantities[item.id];
        if (quantityToServe > 0) {
          const inventoryItem = item.product.inventory.find(
            inv => inv.size === item.size && inv.color === item.color
          );

          if (!inventoryItem) {
            throw new Error(`No se encontró stock para ${item.product.code} (${item.size}, ${item.color})`);
          }

          const maxServable = Math.max(0, (item.preparation_quantity || 0) - (item.delivered_quantity || 0));
          if (quantityToServe > maxServable) {
            throw new Error(`La cantidad a servir para ${item.product.code} (${item.size}, ${item.color}) supera la cantidad preparada disponible.`);
          }

          // Preparar datos para order_items
          const newDeliveredQuantity = item.delivered_quantity + quantityToServe;
          const newPendingQuantity = item.quantity - newDeliveredQuantity;
          const newPreparationQuantity = item.preparation_quantity - quantityToServe;
          
          itemsToProcess.push({
            itemId: item.id,
            newDeliveredQuantity,
            newPendingQuantity,
            newPreparationQuantity,
            newPreparationStatus: newPreparationQuantity > 0 ? 'prepared' : 'completed',
            newDeliveryCount: (item.delivery_count || 0) + 1,
            inventoryId: inventoryItem.id,
            // La preparación ya actúa como reserva lógica; al servir solo dejamos el stock no negativo.
            newInventoryQuantity: Math.max(0, inventoryItem.quantity - quantityToServe)
          });

          // Preparar datos para documentos
          documentItems.push({
            document_id: null, // Se asignará después
            product_id: item.product.id,
            size: item.size,
            color: item.color,
            quantity: quantityToServe,
            price: item.price
          });

          // Preparar datos para entregas
          deliveryItems.push({
            delivery_id: null, // Se asignará después
            order_item_id: item.id,
            quantity_delivered: quantityToServe
          });

          documentTotal += item.price * quantityToServe;

          if (newPendingQuantity > 0) {
            allItemsCompleted = false;
          }
        } else if (item.pending_quantity > 0) {
          allItemsCompleted = false;
        }
      }

      // Crear documento de salida
      const { data: document, error: documentError } = await supabase
        .from('documents')
        .insert({
          type: 'salida',
          date: new Date().toISOString(),
          customer_id: order.customer.id,
          order_id: orderId,
          delivery_type: 'partial',
          total: documentTotal,
          status: 'completed',
          number: `SAL-${new Date().getTime()}`,
          user_id: user.id
        })
        .select()
        .single();

      if (documentError) throw documentError;

      // Crear registro de entrega
      const deliveryNumber = generateDeliveryNumber();
      const { data: delivery, error: deliveryError } = await supabase
        .from('order_deliveries')
        .insert({
          order_id: orderId,
          document_id: document.id,
          delivery_number: deliveryNumber,
          delivery_type: 'partial',
          notes: deliveryNotes,
          created_by: user.id
        })
        .select()
        .single();

      if (deliveryError) throw deliveryError;

      // Asignar IDs a los arrays preparados
      documentItems.forEach(item => item.document_id = document.id);
      deliveryItems.forEach(item => item.delivery_id = delivery.id);

      // Ejecutar todas las operaciones de actualización en paralelo
      const updatePromises = [];

      // Actualizar order_items
      for (const itemData of itemsToProcess) {
        updatePromises.push(
          supabase
            .from('order_items')
            .update({
              delivered_quantity: itemData.newDeliveredQuantity,
              pending_quantity: itemData.newPendingQuantity,
              preparation_quantity: itemData.newPreparationQuantity,
              preparation_status: itemData.newPreparationStatus,
              last_delivery_date: new Date().toISOString(),
              delivery_count: itemData.newDeliveryCount
            })
            .eq('id', itemData.itemId)
        );
      }

      // Actualizar inventario
      for (const itemData of itemsToProcess) {
        updatePromises.push(
          supabase
            .from('inventory')
            .update({ quantity: itemData.newInventoryQuantity })
            .eq('id', itemData.inventoryId)
        );
      }

      // Insertar items del documento
      if (documentItems.length > 0) {
        updatePromises.push(
          supabase.from('document_items').insert(documentItems)
        );
      }

      // Insertar items de entrega
      if (deliveryItems.length > 0) {
        updatePromises.push(
          supabase.from('delivery_items').insert(deliveryItems)
        );
      }

      // Actualizar estado del pedido
      updatePromises.push(
        supabase
          .from('orders')
          .update({ 
            status: allItemsCompleted ? 'served' : 'partially_served',
            preparation_status: allItemsCompleted ? 'completed' : 'partially_prepared'
          })
          .eq('id', orderId)
      );

      // Ejecutar todas las operaciones en paralelo
      const results = await Promise.all(updatePromises);
      
      // Verificar errores
      for (const result of results) {
        if (result.error) throw result.error;
      }

      // Actualizar el historial de entregas
      await fetchDeliveryHistory();

      toast({
        title: "Éxito",
        description: `Entrega ${deliveryNumber} creada exitosamente. ${
          allItemsCompleted 
            ? "El pedido se ha servido completamente"
            : "El pedido se ha servido parcialmente"
        }`,
      });

      // Limpiar formulario
      setQuantities({});
      setDeliveryNotes('');
      
    } catch (error) {
      console.error('Error serving order:', error);
      setError(error.message || "No se pudo servir el pedido");
      toast({
        title: "Error",
        description: error.message || "No se pudo servir el pedido",
        variant: "destructive"
      });
    } finally {
      setServing(false);
    }
  };

  const groupItemsByProduct = () => {
    if (!order) return [];
    
    const groupedItems = {};
    order.order_items.forEach(item => {
      const key = `${item.product.code}-${item.color}`;
      if (!groupedItems[key]) {
        groupedItems[key] = {
          productCode: item.product.code,
          productDescription: item.product.description,
          color: item.color,
          productImage: item.product.images?.[item.product.main_image_index || 0],
          items: []
        };
      }
      groupedItems[key].items.push(item);
    });
    
    return Object.values(groupedItems);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const groupedItems = groupItemsByProduct();
  const totalToServe = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  const totalAmountToServe = calculateDeliveryTotal();

  return (
    <div className="space-y-6 bg-white p-6 max-h-[90vh] flex flex-col">
      {/* Header fijo */}
      <div className="flex justify-between items-start flex-shrink-0">
        <div>
          <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
            <span className="text-blue-600">📦</span>
            Servir Pedido #{order.number}
          </h2>
          <p className="text-gray-600">Cliente: {order.customer?.name}</p>
          <p className="text-sm text-gray-500">Fecha: {new Date(order.date).toLocaleDateString()}</p>
          <div className="flex items-center gap-4 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2"
            >
              <History className="h-4 w-4" />
              {showHistory ? 'Ocultar' : 'Ver'} Historial ({deliveryHistory.length})
            </Button>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="bg-green-50 px-4 py-2 rounded-lg border border-green-200">
            <p className="text-lg font-semibold text-green-700">Total Entrega: ${totalAmountToServe.toFixed(2)}</p>
            <p className="text-sm text-gray-600">Total Pedido: ${order.total?.toFixed(2)}</p>
            {order.discount_percentage > 0 && (
              <p className="text-sm text-green-600">Descuento aplicado: {order.discount_percentage}%</p>
            )}
            {order.payment_transfer && (
              <p className="text-sm text-blue-600">💳 Transferencia</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Contenido scrolleable */}
      <div className="flex-1 overflow-y-auto space-y-6 pr-2">
        {/* Historial de Entregas */}
        {showHistory && deliveryHistory.length > 0 && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Historial de Entregas
            </h3>
            <div className="space-y-3">
              {deliveryHistory.map((delivery) => (
                <div key={delivery.id} className="bg-white p-3 rounded border">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-medium text-blue-600">{delivery.delivery_number}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        {new Date(delivery.delivery_date).toLocaleDateString()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-green-600">
                      ${delivery.document?.total?.toFixed(2)}
                    </span>
                  </div>
                  {delivery.notes && (
                    <p className="text-sm text-gray-600 mb-2">{delivery.notes}</p>
                  )}
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Items entregados:</span>
                    {delivery.delivery_items.map((item, idx) => (
                      <span key={idx} className="ml-2">
                        {item.quantity_delivered}x {item.order_item.product.code} 
                        ({item.order_item.size}, {item.order_item.color})
                        {idx < delivery.delivery_items.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notas de Entrega */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <Label htmlFor="deliveryNotes" className="text-sm font-medium text-gray-700 mb-2 block">
            Notas de Entrega (opcional)
          </Label>
          <Textarea
            id="deliveryNotes"
            placeholder="Agregar notas sobre esta entrega..."
            value={deliveryNotes}
            onChange={(e) => setDeliveryNotes(e.target.value)}
            className="w-full"
            rows={2}
          />
        </div>

        {/* Lista de productos */}
        <div className="space-y-6">
          {groupedItems.map((group) => (
            <div key={`${group.productCode}-${group.color}`} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="mb-4 flex items-start gap-4">
                {group.productImage && (
                  <img 
                    src={group.productImage} 
                    alt={group.productCode}
                    className="w-20 h-20 object-cover rounded-lg shadow-sm"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{group.productCode}</h3>
                    <ColorBadge color={group.color} />
                  </div>
                  <p className="text-sm text-gray-600">{group.productDescription}</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <Table className="w-full">
                  <colgroup>
                    <col className="w-20" />
                    <col className="w-20" />
                    <col className="w-24" />
                    <col className="w-24" />
                    <col className="w-24" />
                    <col className="w-32" />
                  </colgroup>
                  <TableHeader>
                    <TableRow className="bg-blue-50">
                      <TableHead className="font-semibold text-blue-800 px-4 py-4">Talla</TableHead>
                      <TableHead className="font-semibold text-blue-800 px-4 py-4 text-center">Stock</TableHead>
                      <TableHead className="font-semibold text-blue-800 px-4 py-4 text-center">Pendiente</TableHead>
                      <TableHead className="font-semibold text-blue-800 px-4 py-4 text-center">Preparado</TableHead>
                      <TableHead className="font-semibold text-blue-800 px-4 py-4 text-center">Entregado</TableHead>
                      <TableHead className="font-semibold text-blue-800 px-4 py-4 text-center">A Servir</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.items.map((item) => {
                      const inventoryItem = item.product.inventory.find(
                        inv => inv.size === item.size && inv.color === item.color
                      );
                      const stockAvailable = inventoryItem?.quantity || 0;
                      const hasStockIssue = stockAvailable < item.pending_quantity;

                      return (
                        <TableRow key={item.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium px-4 py-4">{item.size}</TableCell>
                          <TableCell className={`px-4 py-4 text-center ${hasStockIssue ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}`}>
                            {stockAvailable}
                          </TableCell>
                          <TableCell className="px-4 py-4 text-center font-medium">{item.pending_quantity}</TableCell>
                          <TableCell className="px-4 py-4 text-center text-blue-600 font-semibold">{item.preparation_quantity}</TableCell>
                          <TableCell className="px-4 py-4 text-center text-green-600 font-semibold">{item.delivered_quantity}</TableCell>
                          <TableCell className="px-4 py-4 text-center">
                            <div className="space-y-1">
                              <Input
                                type="number"
                                min="0"
                                max={Math.min(item.preparation_quantity, stockAvailable)}
                                value={quantities[item.id] || 0}
                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                className="w-24 border-2 focus:border-blue-500 focus:ring-blue-500 text-center font-medium"
                                disabled={item.pending_quantity === 0 || stockAvailable === 0}
                              />
                              {(quantities[item.id] || 0) > 0 && (
                                <div className="text-sm text-green-600 font-semibold bg-green-50 px-2 py-1 rounded">
                                  ${((quantities[item.id] || 0) * item.price).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>

        {/* Resumen de Entrega */}
        {totalToServe > 0 && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-blue-800">Resumen de Entrega</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-blue-700">
              <div>
                <p>Items a entregar: <span className="font-bold">{totalToServe}</span></p>
              </div>
              <div>
                <p>Importe de entrega: <span className="font-bold">${totalAmountToServe.toFixed(2)}</span></p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer fijo con botones */}
      <div className="flex justify-end gap-4 pt-4 border-t bg-gray-50 p-4 rounded-lg flex-shrink-0">
        <Button variant="outline" onClick={onClose} className="hover:bg-gray-100">
          Cerrar
        </Button>
        <Button 
          onClick={handleServe}
          disabled={serving || !Object.values(quantities).some(qty => qty > 0)}
          className="bg-green-600 hover:bg-green-700 text-white shadow-lg"
          title="Crea la entrega con las cantidades especificadas y actualiza el inventario"
        >
          {serving ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Procesando Entrega...</span>
            </div>
          ) : (
            'Crear Entrega'
          )}
        </Button>
      </div>
    </div>
  );
} 
